// Document Type Configuration - UNIFIED APPROACH
// Manages document types (SOP, Policy, Procedure, Form, etc.)
// Based on Prisma DocumentType model schema

export const documentTypeConfig = {
  entityName: 'DocumentType',
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/document-type',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      // Document Type specific endpoints
      activate: (id) => `/${id}/activate`,
      deactivate: (id) => `/${id}/deactivate`,
      reorder: '/reorder'
    },
    lookupEndpoints: {
      // Document types typically don't have lookup dependencies
      // but you could add categories, templates, etc.
    }
  },
  
  // UI Configuration
  ui: {
    drawerWidth: '600px',
    defaultPageSize: 15,
    pageSizeOptions: [15, 25, 50, 100],
    defaultTableDensity: 'normal',
    requiredFieldStyle: 'badge'
  },
  
  // Service configuration
  service: {
    name: 'documentTypeService',
    methods: {
      getAll: 'getAllDocumentTypes',
      getById: 'getDocumentTypeById', 
      create: 'createDocumentType',
      update: 'updateDocumentType',
      delete: 'deleteDocumentType',
      validate: 'validateData'
    }
  },

  // UNIFIED FIELD CONFIGURATION
  fields: [
    // Basic Information
    { 
      name: 'id', 
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    { 
      name: 'name', 
      label: 'Type Name', 
      type: 'text', 
      required: true,
      table: { header: 'Type Name', width: '200px', sortable: true },
      validation: { 
        required: true, 
        minLength: 2, 
        maxLength: 50,
        pattern: /^[A-Za-z\s\-&/()]+$/,
        message: 'Type name must contain only letters, spaces, hyphens, ampersands, forward slashes, and parentheses'
      },
      placeholder: 'e.g., SOP, Policy, Procedure, Form'
    },
    { 
      name: 'description', 
      label: 'Description', 
      type: 'textarea', 
      required: false,
      table: { header: 'Description', width: '300px', sortable: false },
      validation: { 
        required: false, 
        maxLength: 255 
      },
      placeholder: 'Brief description of this document type and its purpose',
      rows: 3
    },
    { 
      name: 'color', 
      label: 'Display Color', 
      type: 'color', 
      required: false,
      placeholder: 'Choose a color for categorization',
      table: { 
        header: 'Color', 
        width: '100px', 
        sortable: false,
        cellRenderer: 'color'
      },
      validation: { 
        required: false,
        pattern: /^(#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})|[a-z]+)$/,
        message: 'Please enter a valid color (hex code or color name)'
      },
      helperText: 'Used for visual categorization in lists and reports'
    },
    { 
      name: 'record_order', 
      label: 'Sort Order', 
      type: 'number', 
      required: false,
      table: { header: 'Order', width: '80px', sortable: true },
      validation: { 
        required: false, 
        min: 0, 
        max: 1000 
      },
      placeholder: 'Display order (0-1000)',
      helperText: 'Lower numbers appear first in lists'
    },
    {
      name: 'isDocument',
      label: 'Documents module',
      type: 'switch',
      required: false,
      defaultValue: false,
      table: {
        header: 'isDocument',
        width: '110px',
        sortable: true,
        cellRenderer: 'boolean',
      },
      validation: { required: false },
      switchLabel: 'Show in Documents module',
      description: 'When enabled, this type appears in the Documents module',
    },
    {
      name: 'isProcedure',
      label: 'Procedures module',
      type: 'switch',
      required: false,
      defaultValue: false,
      table: {
        header: 'isProcedure',
        width: '110px',
        sortable: true,
        cellRenderer: 'boolean',
      },
      validation: { required: false },
      switchLabel: 'Show in Procedures module',
      description: 'When enabled, this type appears in the Procedures module',
    },
    { 
      name: 'isActive', 
      label: 'Active', 
      type: 'switch', 
      required: false,
      defaultValue: true,
      table: { 
        header: 'Active', 
        width: '80px', 
        sortable: true,
        cellRenderer: 'boolean'
      },
      validation: { required: false },
      switchLabel: 'Document type is active and available for use',
      description: 'Inactive types cannot be assigned to new documents'
    }

    // Note: DocumentType model doesn't have createdAt/updatedAt fields
    // If timestamps are needed, they should be added to the Prisma schema
  ],

  // Table Configuration - Dynamic getter that generates columns from fields
  get tableColumns() {
    return this.fields
      .filter(field => field.table !== false && !field.hidden)
      .map(field => ({
        key: field.name,
        header: field.table?.header || field.label,
        width: field.table?.width || 'auto',
        sortable: field.table?.sortable !== false,
        displayField: field.table?.displayField || field.name,
        cellRenderer: field.table?.cellRenderer || 'default'
      }));
  },

  // Filter Configuration
  filterableFields: ['name', 'isDocument', 'isProcedure', 'isActive'],

  // Filter Groups for Basic/Advanced tabs
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic Filters',
      fields: ['name', 'isDocument', 'isProcedure', 'isActive']
    },
  ],

  // Custom Sort Orders
  customSortOrders: {
    isDocument: [true, false],
    isProcedure: [true, false],
    isActive: [true, false],
  },

  // Messages Configuration
  messages: {
    loading: 'Loading document types...',
    noData: 'No document types found. Click "Add Type" to create your first document type.',
    loadError: 'Failed to load document types',
    saveSuccess: 'Document type saved successfully!',
    saveError: 'Failed to save document type',
    deleteSuccess: 'Document type deleted successfully!',
    deleteError: 'Failed to delete document type',
    deleteConfirm: 'Are you sure you want to delete this document type? This action cannot be undone and may affect existing documents.'
  },

  formSections: [
    {
      title: 'Basic Information',
      icon: '📄',
      fields: ['name', 'description'],
    },
    {
      title: 'Module visibility',
      icon: '📂',
      fields: ['isDocument', 'isProcedure'],
    },
    {
      title: 'Display & Settings',
      icon: '⚙️',
      fields: ['color', 'record_order', 'isActive'],
    },
  ],

  // Labels Configuration
  labels: {
    // Using predefined labels + custom overrides
    singular: 'Document Type',
    plural: 'Document Types',
    addButton: 'Add Type',
    editButton: 'Edit Type',
    deleteButton: 'Delete Type',
    
    // Custom labels for document types
    pageTitle: 'Document Type Management',
    pageSubtitle: 'Manage document categories and classifications',
    searchPlaceholder: 'Search document types...',
    
    // Form labels
    nameLabel: 'Type Name',
    descriptionLabel: 'Description',
    colorLabel: 'Display Color',
    orderLabel: 'Sort Order',
    activeLabel: 'Active Status'
  },

  transformData: (data) => {
    const transformed = { ...data };
    ['isActive', 'isDocument', 'isProcedure'].forEach((field) => {
      if (transformed[field] !== undefined) {
        if (typeof transformed[field] === 'string') {
          transformed[field] = transformed[field] === 'true';
        } else {
          transformed[field] = Boolean(transformed[field]);
        }
      }
    });
    if (transformed.record_order !== undefined && transformed.record_order !== '') {
      const n = parseInt(transformed.record_order, 10);
      if (!Number.isNaN(n)) transformed.record_order = n;
      else delete transformed.record_order;
    } else if (transformed.record_order === '') {
      delete transformed.record_order;
    }
    return transformed;
  },

  // Fields to remove during data preparation for submission (used by BaseService)
  fieldsToRemove: [
    'documents' // Remove relations (no timestamp fields in DocumentType model)
  ],

  // Export filename for data exports
  exportFileName: 'document_types',

  // Validation function for real-time field validation (dynamically assigned)
  validateField: null // Will be set after validation module loads
};
